import type { CollectionEntry } from '../types';
import { normalizeSocialLinks, type SocialLinks } from '../constants/socialPlatforms';
import { TEAM_TAG_REGEX } from '../features/teams/types';

export interface PublicProfile {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
  avatarUrl: string | null;
  backgroundImageUrl: string | null;
  bio: string;
  socialLinks: SocialLinks;
  countryCode: string | null;
  /** Denormalized 2–5 character team tag when the player is on a team. */
  teamSlug: string | null;
  /** Explicitly featured public deck on the player profile hero. */
  featuredDeckId: string | null;
  slug: string;
  /** True when the user has opted-in to a public card collection. */
  collectionIsPublic: boolean;
}

export interface PublicProfilePayload {
  profile: PublicProfile;
  /** Bag entries when the collection is public; null when it is not. */
  entries: Record<string, CollectionEntry> | null;
}

/**
 * `/players/:slug` and `/collections/:slug` — one crawlable URL per registered
 * account, and bots re-request the same fixed set in bursts.
 *
 * Served from `/api/public-collection` (a vercel.json rewrite onto the sitemap
 * function, which supplies `action=public-collection` — do not send `action`
 * here). A CDN hit costs no Firestore reads at all; a miss costs one directory
 * read plus, only for the ~2% of accounts with a public collection, the gate
 * and the bag.
 */
const PROFILE_TTL_MS = 5 * 60 * 1000;

type CacheRow = { at: number; value: PublicProfilePayload | null };

/** Per-tab memo so the profile page and collection page share one request. */
const cache = new Map<string, CacheRow>();
const inFlight = new Map<string, Promise<PublicProfilePayload | null>>();

export type PublicProfilePatch = Partial<Pick<PublicProfile, 'featuredDeckId'>>;

type PublicProfilePatchListener = (slug: string, patch: PublicProfilePatch) => void;

const patchListeners = new Set<PublicProfilePatchListener>();

export function clearPublicProfileCache(): void {
  cache.clear();
  inFlight.clear();
}

/**
 * Owner edits (featured deck) write Firestore directly. Patch the tab cache and
 * notify mounted profile pages so they don't wait out the CDN / client TTL.
 */
export function patchPublicProfileCache(slug: string, patch: PublicProfilePatch): void {
  const key = slug.trim().toLowerCase();
  if (!key) return;

  const row = cache.get(key);
  if (row?.value) {
    cache.set(key, {
      at: row.at,
      value: {
        ...row.value,
        profile: { ...row.value.profile, ...patch },
      },
    });
  }

  for (const listener of patchListeners) listener(key, patch);
}

export function subscribePublicProfilePatch(listener: PublicProfilePatchListener): () => void {
  patchListeners.add(listener);
  return () => {
    patchListeners.delete(listener);
  };
}

function parseEntries(raw: unknown): Record<string, CollectionEntry> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, CollectionEntry> = {};
  for (const [cardId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const row = value as Record<string, unknown>;
    const quantity = Math.max(0, Math.floor(Number(row.quantity) || 0));
    if (!quantity) continue;
    const updatedAt = Number(row.updatedAt) || Date.now();
    out[cardId] = {
      cardId,
      quantity,
      quantityFoil: Math.min(Math.max(0, Math.floor(Number(row.quantityFoil) || 0)), quantity),
      notes: typeof row.notes === 'string' && row.notes ? row.notes : undefined,
      addedAt: Number(row.addedAt) || updatedAt,
      updatedAt,
    };
  }
  return out;
}

function parsePayload(slug: string, raw: unknown): PublicProfilePayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Record<string, unknown>;
  const rawProfile = body.profile;
  if (!rawProfile || typeof rawProfile !== 'object') return null;
  const p = rawProfile as Record<string, unknown>;
  if (typeof p.uid !== 'string' || !p.uid) return null;

  const teamSlug = typeof p.teamSlug === 'string' ? p.teamSlug.trim().toLowerCase() : '';
  return {
    profile: {
      uid: p.uid,
      displayName: (p.displayName as string | null) ?? null,
      photoURL: (p.photoURL as string | null) ?? null,
      avatarUrl: (p.avatarUrl as string | null) ?? null,
      backgroundImageUrl: (p.backgroundImageUrl as string | null) ?? null,
      bio: typeof p.bio === 'string' ? p.bio : '',
      socialLinks: normalizeSocialLinks(p.socialLinks),
      countryCode: (p.countryCode as string | null) ?? null,
      teamSlug: TEAM_TAG_REGEX.test(teamSlug) ? teamSlug : null,
      featuredDeckId:
        typeof p.featuredDeckId === 'string' && p.featuredDeckId.trim()
          ? p.featuredDeckId.trim()
          : null,
      slug,
      collectionIsPublic: p.collectionIsPublic === true,
    },
    entries: parseEntries(body.entries),
  };
}

/** `null` means the handle does not exist. Throws on transport failure. */
export async function fetchPublicProfile(
  slug: string,
  signal?: AbortSignal,
): Promise<PublicProfilePayload | null> {
  const key = slug.trim().toLowerCase();
  if (!key) return null;

  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < PROFILE_TTL_MS) return cached.value;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = (async () => {
    const res = await fetch(`/api/public-collection?slug=${encodeURIComponent(key)}`, { signal });
    if (res.status === 404) {
      cache.set(key, { at: Date.now(), value: null });
      return null;
    }
    if (!res.ok) throw new Error(`Public profile failed (${res.status})`);
    const value = parsePayload(key, await res.json());
    cache.set(key, { at: Date.now(), value });
    return value;
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    inFlight.delete(key);
  }
}
